"use client";

import { ADVERTISE_CONTENT_URL } from "@/constant";
import useAxiosQuery from "@/hooks/useMyQuery";
import type { BlogData } from "@/types/news";
import type { FC } from "react";
import { useEffect, useState, useRef } from "react";
import { Skeleton, Button } from "antd";
import { sanitizeHtmlContent } from "@/helpers";
import { scrollElementIntoView } from "@/utils/blogUtils";
import classNames from "classnames";
import Image from "next/image";
import { useTranslations } from "next-intl";
import banner from "@/asset/images/advertise-banner.png";
import { useSearchParams } from "next/navigation";
import Link from "next/link";

interface Heading {
  id: string;
  level: number;
  text: string;
  element?: Element;
}

// Function to extract headings from HTML content
const extractHeadings = (html: string): Heading[] => {
  // In browser environments, we can use an actual DOM element
  if (typeof document !== "undefined") {
    // Get headings directly from the DOM when possible
    const blogContentElement = document.getElementById("blog-content");
    if (blogContentElement) {
      const headingElements = blogContentElement.querySelectorAll(
        "h1, h2, h3, h4, h5, h6"
      );

      return Array.from(headingElements).map((heading, index) => {
        // Ensure heading has an ID
        if (!heading.id) {
          heading.id = `heading-${index}`;
        }

        return {
          id: heading.id,
          level: parseInt(heading.tagName.substr(1), 10),
          text: heading.textContent?.trim() || "",
          element: heading,
        };
      });
    }
  }

  // Fallback to parsing HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const headings = Array.from(doc.querySelectorAll("h1, h2, h3, h4, h5, h6"));

  return headings.map((heading, index) => ({
    id: heading.id || `heading-${index}`,
    level: parseInt(heading.tagName.substr(1), 10),
    text: heading.textContent?.trim() || "",
    element: heading,
  }));
};

interface TableOfContentsProps {
  headings: Heading[];
  activeId: string | null;
  onClick: (id: string) => void;
}

const TableOfContents: FC<TableOfContentsProps> = ({
  headings,
  activeId,
  onClick,
}) => {
  if (!headings || headings.length === 0) {
    return null;
  }

  return (
    <ul className='space-y-2'>
      {headings.map((heading) => (
        <li
          key={heading.id}
          className={classNames(
            "cursor-pointer hover:text-primary transition-colors",
            {
              "text-primary font-extrabold": activeId === heading.id,
              "text-black": activeId !== heading.id,
              "pl-0 font-extrabold": heading.level === 1,
              "pl-2 font-bold": heading.level === 2,
              "pl-4 font-bold": heading.level === 3,
              "pl-6 text-gray-500": heading.level === 4,
              "pl-8 text-gray-500": heading.level === 5 || heading.level === 6,
            }
          )}
          onClick={() => onClick(heading.id)}>
          {heading.text}
        </li>
      ))}
    </ul>
  );
};

const AdvertiseContent: FC = () => {
  const searchParams = useSearchParams();
  const headingParam = searchParams.get("heading");

  const { data: advertiseRes, isLoading } = useAxiosQuery<{
    data: BlogData;
  }>({
    url: `/v1/blog/detail/${ADVERTISE_CONTENT_URL}`,
    method: "get",
  });

  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeHeading, setActiveHeading] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tocRef = useRef<HTMLDivElement>(null);
  const blogData = advertiseRes?.data;
  const [isScrollingComplete, setIsScrollingComplete] = useState(false);

  // Reset scroll state when heading param changes
  useEffect(() => {
    setIsScrollingComplete(false);
  }, [headingParam]);

  // Initialize blog content from lexical first
  useEffect(() => {
    if (blogData?.content) {
      const blogContentElement = document.getElementById("blog-content");
      if (blogContentElement) {
        blogContentElement.innerHTML = sanitizeHtmlContent(blogData.content);

        // Now that content is populated, we can process headings
        setTimeout(() => {
          const headingElements = blogContentElement.querySelectorAll(
            "h1, h2, h3, h4, h5, h6"
          );

          // Add IDs to headings if they don't have them
          headingElements.forEach((heading, index) => {
            if (!heading.id) {
              heading.id = `heading-${index}`;
            }
          });

          const extractedHeadings = extractHeadings(
            blogContentElement.innerHTML
          );
          setHeadings(extractedHeadings);
        }, 0);
      }
    }
  }, [blogData?.content]);

  // Scroll to heading based on URL param after headings are processed
  useEffect(() => {
    if (headings.length > 0 && headingParam && !isScrollingComplete) {
      // Decode the URL parameter to handle special characters
      const decodedParam = decodeURIComponent(headingParam);

      const normalizedParamText = decodedParam.toLowerCase().trim();

      // Handle special case for "Social Media & Community Management"
      // URLs might encode & as &amp; or just & depending on the context
      const specialCaseParam = normalizedParamText.replace(/&amp;/g, "&");

      // Try to find the heading with multiple strategies
      let headingToScrollTo = null;

      // 1. Try exact match (case insensitive)
      headingToScrollTo = headings.find(
        (heading) =>
          heading.text.toLowerCase().trim() === normalizedParamText ||
          heading.text.toLowerCase().trim() === specialCaseParam
      );

      // 2. If no exact match, try exact match ignoring spaces
      if (!headingToScrollTo) {
        const cleanParam1 = normalizedParamText.replace(/\s+/g, "");
        const cleanParam2 = specialCaseParam.replace(/\s+/g, "");

        headingToScrollTo = headings.find((heading) => {
          const cleanHeading = heading.text.toLowerCase().replace(/\s+/g, "");
          return cleanHeading === cleanParam1 || cleanHeading === cleanParam2;
        });
      }

      // 3. If still no match, try includes (more lenient)
      if (!headingToScrollTo) {
        headingToScrollTo = headings.find((heading) => {
          const normalizedHeading = heading.text.toLowerCase().trim();
          return (
            normalizedHeading.includes(normalizedParamText) ||
            normalizedHeading.includes(specialCaseParam) ||
            normalizedParamText.includes(normalizedHeading) ||
            specialCaseParam.includes(normalizedHeading)
          );
        });
      }

      if (headingToScrollTo) {
        // Scroll to the heading
        const element = document.getElementById(headingToScrollTo.id);
        if (element) {
          // Mark as complete to prevent repeated scrolling
          setIsScrollingComplete(true);

          // Use a longer timeout to ensure the page is fully rendered
          setTimeout(() => {
            scrollElementIntoView(element, 160);
            setActiveHeading(headingToScrollTo.id);
          }, 500);
        } else {
          console.log("Element not found in DOM:", headingToScrollTo.id);
        }
      } else {
        // Mark as complete even if no match found to prevent continuous retries
        setIsScrollingComplete(true);
      }
    }
  }, [headings, headingParam, isScrollingComplete]);

  // Set up intersection observer to track active heading
  useEffect(() => {
    if (headings.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveHeading(entry.target.id);
            }
          });
        },
        { rootMargin: "-100px 0px -80% 0px" }
      );

      headings.forEach((heading) => {
        const element = document.getElementById(heading.id);
        if (element) {
          observer.observe(element);
        }
      });

      return () => {
        headings.forEach((heading) => {
          const element = document.getElementById(heading.id);
          if (element) {
            observer.unobserve(element);
          }
        });
      };
    }
  }, [headings]);

  // Handle click on TOC item
  const handleTocItemClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      scrollElementIntoView(element, 150);
      setActiveHeading(id);
    }
  };

  // Setup scroll event to handle TOC position and overflow
  useEffect(() => {
    const handleScrollAndResize = () => {
      if (!tocRef.current) return;

      const headerHeight = 150; // From your layout component (lg:pt-[114px])
      const footerElement = document.querySelector("footer");

      if (footerElement) {
        const footerRect = footerElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Calculate available height when footer is visible
        if (footerRect.top < viewportHeight) {
          const availableHeight = footerRect.top - headerHeight - 20; // 20px buffer
          tocRef.current.style.maxHeight = `${availableHeight}px`;
        } else {
          // Default max height when footer is not in view
          tocRef.current.style.maxHeight = `calc(100vh - ${headerHeight}px - 40px)`;
        }
      }
    };

    // Initial setup
    handleScrollAndResize();

    // Add event listeners
    window.addEventListener("scroll", handleScrollAndResize);
    window.addEventListener("resize", handleScrollAndResize);

    return () => {
      window.removeEventListener("scroll", handleScrollAndResize);
      window.removeEventListener("resize", handleScrollAndResize);
    };
  }, []);

  if (isLoading) {
    return (
      <div className='flex gap-8 max-w-[1280px] mx-auto my-8'>
        <div className='w-64'>
          <Skeleton active paragraph={{ rows: 20 }} />
        </div>
        <div className='flex-1'>
          <Skeleton active paragraph={{ rows: 20 }} />
        </div>
      </div>
    );
  }
  return (
    <div className='flex max-w-[1280px] mx-auto my-8' id='lexical-blog-detail'>
      {/* Left column with sticky TOC */}
      <div
        ref={tocRef}
        className=' hidden md:block sticky top-5 left-5 pl-4 w-64 overflow-auto p-4 border border-gray-200 rounded-[6px] bg-gray-50'
        style={{ maxHeight: "calc(100vh - 40px)" }} // Initial height
      >
        {/* TOC content in a scrollable container */}
        <div className='flex-1 overflow-auto mb-4'>
          <TableOfContents
            headings={headings}
            activeId={activeHeading}
            onClick={handleTocItemClick}
          />
        </div>

        {/* Fixed button at the bottom */}
        <div className='sticky bottom-0 bg-gray-50 pt-2 w-full'>
          <Button
            type='primary'
            className='w-full text-xs'
            onClick={() =>
              window.open(`https://klarda.com/en/registration-portal`)
            }>
            Need Consultation? Contact us.
          </Button>
        </div>
      </div>

      {/* Blog Content (Right side) */}
      <div className='flex-1' ref={contentRef}>
        <div id='blog-content' className='prose max-w-none mx-8' />

        {/* Mobile Table of Contents */}
        <div className='block lg:hidden mt-8 mb-8 mx-8'>
          <TableOfContents
            headings={headings}
            activeId={activeHeading}
            onClick={handleTocItemClick}
          />
        </div>
      </div>
    </div>
  );
};

export function KlardaAdvertise() {
  return (
    <>
      <div className='relative'>
        <Image
          src={banner.src}
          width={1920}
          height={384}
          alt='advertise banner'
          className=''
        />
        <div className='absolute bottom-[20%] md:bottom-[30%] left-1/2 -translate-x-1/2 text-center'>
          <h1 className='text-[20px] md:text-[30px] lg:text-[60px] font-extrabold text-nowrap'>
            Work With Klarda Media
          </h1>
          <Link
            href='https://klarda.com/en/registration-portal'
            target='_blank'>
            <Button
              className='h-8 md:h-10 lg:h-12 text-sm md:text-base'
              type='primary'>
              Contact Us
            </Button>
          </Link>
        </div>
      </div>
      <AdvertiseContent />
    </>
  );
}
